import type { JSX } from 'react';

import { Pencil, Play, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import type { Webhook } from '../types/webhook';
import { detectWebhookPlatform } from '../utils/detectWebhookPlatform';

const TRUNCATE = {
  NAME: 75,
} as const;

const truncateText = (text: string, maxLen: number): string => {
  if (text.length <= maxLen) {
    return text;
  }

  return `${text.slice(0, maxLen)}…`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface WebhooksTableProps {
  onDelete: (webhook: Webhook) => void;
  onEdit: (webhook: Webhook) => void;
  onTest: (webhook: Webhook) => void;
  webhooks: Webhook[];
}

const WebhooksTable = ({ onDelete, onEdit, onTest, webhooks }: WebhooksTableProps): JSX.Element => {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-left text-sm font-body">
        <thead>
          <tr className="border-b border-border-subtle">
            <th className="py-2 pr-3 font-medium text-text-muted">Nombre</th>
            <th className="py-2 pr-3 font-medium text-text-muted">URL</th>
            <th className="py-2 pr-3 font-medium text-text-muted">Plataforma</th>
            <th className="py-2 pr-3 font-medium text-text-muted">Proyecto</th>
            <th className="py-2 pr-3 font-medium text-text-muted">Estado</th>
            <th className="py-2 pr-3 font-medium text-text-muted">Creado</th>
            <th className="py-2 text-right font-medium text-text-muted">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {webhooks.map((webhook) => (
            <WebhooksTableRow key={webhook.id} webhook={webhook} onDelete={onDelete} onEdit={onEdit} onTest={onTest} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface WebhooksTableRowProps {
  onDelete: (webhook: Webhook) => void;
  onEdit: (webhook: Webhook) => void;
  onTest: (webhook: Webhook) => void;
  webhook: Webhook;
}

const WebhooksTableRow = ({ onDelete, onEdit, onTest, webhook }: WebhooksTableRowProps): JSX.Element => {
  const platform = detectWebhookPlatform(webhook.url);

  return (
    <tr className="border-b border-border-subtle last:border-b-0">
      <td
        className={cn(
          'max-w-[200px] py-2 pr-3 align-middle font-medium',
          webhook.enabled ? 'text-text-default' : 'text-text-muted'
        )}
        title={webhook.name.length > TRUNCATE.NAME ? webhook.name : undefined}
      >
        {truncateText(webhook.name, TRUNCATE.NAME)}
      </td>
      <td
        className={cn(
          'max-w-[300px] py-2 pr-3 align-middle font-mono text-xs',
          webhook.enabled ? 'text-text-default' : 'text-text-muted'
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="block max-w-full cursor-default truncate">{webhook.url}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-sm break-all whitespace-normal">
            {webhook.url}
          </TooltipContent>
        </Tooltip>
      </td>
      <td className="py-2 pr-3 align-middle">
        {platform ? (
          <span
            className="inline-block rounded-full px-2 py-0.5 text-[0.7rem] font-semibold text-white"
            style={{ backgroundColor: platform.color }}
          >
            {platform.name}
          </span>
        ) : (
          <span className="inline-block rounded-full border border-border-subtle px-2 py-0.5 text-[0.7rem] text-text-muted">
            Otro
          </span>
        )}
      </td>
      <td className="py-2 pr-3 align-middle">
        {webhook.subProject ? (
          <span>{webhook.subProject.name}</span>
        ) : (
          <span className="text-text-muted italic">General</span>
        )}
      </td>
      <td className="py-2 pr-3 align-middle">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-[0.7rem] font-medium',
            webhook.enabled ? 'bg-success/15 text-success' : 'border border-border-subtle text-text-muted'
          )}
        >
          {webhook.enabled ? 'Habilitado' : 'Deshabilitado'}
        </span>
      </td>
      <td className="py-2 pr-3 align-middle text-text-muted">{formatDate(webhook.createdAt)}</td>
      <td className="py-2 align-middle">
        <div className="flex justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="text-brand-vivid"
                onClick={() => {
                  onTest(webhook);
                }}
                aria-label="Probar webhook"
              >
                <Play className="size-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Probar webhook</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  onEdit(webhook);
                }}
                aria-label="Editar webhook"
              >
                <Pencil className="size-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Editar</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  onDelete(webhook);
                }}
                aria-label="Eliminar webhook"
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Eliminar</TooltipContent>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

export default WebhooksTable;
